import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';

export class R1C1LocationIndexes
{
    public constructor(public readonly colIndex:number,
                       public readonly rowIndex:number)
    {
    }
}

export class R1C1RangeIndexes
{
    public constructor(public readonly startIndexes:R1C1LocationIndexes,
                       public readonly endIndexes:R1C1LocationIndexes)
    {
    }
}

export class R1C1ColumnOrRowLocation
{
    public constructor(public readonly offset:number,
                       public readonly isFixed:boolean)
    {
    }
}

export class R1C1Location
{
    public constructor(public readonly rowOffset:number,
                       public readonly isRowFixed:boolean,
                       public readonly columnOffset:number,
                       public readonly isColumnFixed:boolean)
    {
    }

    public getIndexes(currentColumnIndex:number, currentRowIndex:number):R1C1LocationIndexes
    {
        const colIndex:number = this.columnOffset != null
            ? this.isColumnFixed
                ? this.columnOffset - 1
                : currentColumnIndex + this.columnOffset
            : null;
        const rowIndex:number = this.rowOffset != null
            ? this.isRowFixed
                ? this.rowOffset - 1
                : currentRowIndex + this.rowOffset
            : null;

        return new R1C1LocationIndexes(colIndex, rowIndex);
    }

    public isEqualTo(other:R1C1Location):boolean
    {
        return !!other
            && this.rowOffset === other.rowOffset
            && this.isRowFixed === other.isRowFixed
            && this.columnOffset === other.columnOffset
            && this.isColumnFixed === other.isColumnFixed;
    }

    public toString():string
    {
        const parts:Array<string> = [];

        if( this.rowOffset != null )
        {
            parts.push('R');

            if( !this.isRowFixed && this.rowOffset !== 0 )
                parts.push('[');

            if( this.rowOffset !== 0 )
                parts.push(this.rowOffset.toString());

            if( !this.isRowFixed && this.rowOffset !== 0 )
                parts.push(']');
        }

        if( this.columnOffset != null )
        {
            parts.push('C');

            if( !this.isColumnFixed && this.columnOffset !== 0 )
                parts.push('[');

            if( this.columnOffset !== 0 )
                parts.push(this.columnOffset.toString());

            if( !this.isColumnFixed && this.columnOffset !== 0 )
                parts.push(']');
        }

        return parts.join('');
    }

    public toA1String(currentColumnIndex:number, currentRowIndex:number):string
    {
        const parts:Array<string> = [];

        if( this.columnOffset != null )
        {
            const text = this.isColumnFixed
                ? MeshLocationUtils.getLetterByColumn(this.columnOffset)
                : MeshLocationUtils.getLetterByColumnIndex(currentColumnIndex + this.columnOffset);

            if( text.length > 0 && this.isColumnFixed )
                parts.push('$');

            parts.push(text);
        }

        if( this.rowOffset != null )
        {
            const text = String(this.isRowFixed
                ? this.rowOffset
                : currentRowIndex + this.rowOffset + 1);

            if( text.length > 0 && this.isRowFixed )
                parts.push('$');

            parts.push(text);
        }

        return parts.join('');
    }
}

export class R1C1Range
{
    public constructor(public readonly startLocation:R1C1Location,
                       public readonly endLocation:R1C1Location)
    {
    }

    public getNormalizedRange(currentColumnIndex:number, currentRowIndex:number):R1C1Range
    {
        let startCol:number = this.startLocation ? this.startLocation.columnOffset : null;
        let isStartColFixed:boolean = !!this.startLocation && this.startLocation.isColumnFixed;
        let startRow:number = this.startLocation ? this.startLocation.rowOffset : null;
        let isStartRowFixed:boolean = !!this.startLocation && this.startLocation.isRowFixed;
        let endCol:number = this.endLocation ? this.endLocation.columnOffset : null;
        let isEndColFixed:boolean = !!this.endLocation && this.endLocation.isColumnFixed;
        let endRow:number = this.endLocation ? this.endLocation.rowOffset : null;
        let isEndRowFixed:boolean = !!this.endLocation && this.endLocation.isRowFixed;

        const startIndexes:R1C1LocationIndexes = this.startLocation
            ? this.startLocation.getIndexes(currentColumnIndex, currentRowIndex)
            : null;
        const endIndexes:R1C1LocationIndexes = this.endLocation
            ? this.endLocation.getIndexes(currentColumnIndex, currentRowIndex)
            : null;

        if( (startCol != null && endCol != null
            && startIndexes && endIndexes && startIndexes.colIndex - endIndexes.colIndex > 0)
            || (startCol == null && endCol != null) )
        {
            [startCol, endCol] = [endCol, startCol];
            [isStartColFixed, isEndColFixed] = [isEndColFixed, isStartColFixed];
        }

        if( (startRow != null && endRow != null
            && startIndexes && endIndexes && startIndexes.rowIndex - endIndexes.rowIndex > 0)
            || (startRow == null && endRow != null) )
        {
            [startRow, endRow] = [endRow, startRow];
            [isStartRowFixed, isEndRowFixed] = [isEndRowFixed, isStartRowFixed];
        }

        return new R1C1Range(
            new R1C1Location(startRow, isStartRowFixed, startCol, isStartColFixed),
            new R1C1Location(endRow, isEndRowFixed, endCol, isEndColFixed),
        );
    }

    public getIndexes(currentColumnIndex:number, currentRowIndex:number):R1C1RangeIndexes
    {
        return new R1C1RangeIndexes(
            this.startLocation
                ? this.startLocation.getIndexes(currentColumnIndex, currentRowIndex)
                : null,
            this.endLocation
                ? this.endLocation.getIndexes(currentColumnIndex, currentRowIndex)
                : null,
        );
    }

    public isEqualTo(other:R1C1Range):boolean
    {
        return !!other
            && this.getNormalizedRange(
                0,
                0,
            ).toString() === other.getNormalizedRange(
                0,
                0,
            ).toString();
    }

    public toString():string
    {
        return `${this.startLocation
            ? this.startLocation.toString()
            : ''}:${this.endLocation
            ? this.endLocation.toString()
            : ''}`;
    }

    public toA1String(currentColumnIndex:number, currentRowIndex:number):string
    {
        return `${this.startLocation
            ? this.startLocation.toA1String(currentColumnIndex, currentRowIndex)
            : ''}:${this.endLocation
            ? this.endLocation.toA1String(currentColumnIndex, currentRowIndex)
            : ''}`;
    }
}
